import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import debounce from 'lodash.debounce';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const persistToLocalStorage = debounce((width) => {
  try {
    localStorage.setItem('resizable-area-width', width);
  } catch (error) {
    console.log('error saving to local storage');
  }
}, 500);

const setHandleHeight = (element, reset) => {
  if (!element) return;

  const parent = element?.parentElement;

  if (parent) {
    element.style.height = '0px';

    const scrollHeight = parent.scrollHeight;
    element.style.height = `${scrollHeight}px`;
  }
};

const handleWindowResize = debounce((handleElement, callback) => {
  if (window.innerWidth < 768) callback(0);

  setHandleHeight(handleElement);
}, 500);

const Resizable = ({ children, handleRef, disabled }) => {
  const [width, setWidth] = useState(0); // Initial width of the div
  const [isResizing, setIsResizing] = useState(false);
  const resizableRef = useRef(null);

  const isTouchDevice = window?.matchMedia('(pointer: coarse)')?.matches;

  const handleMouseDown = (e) => {
    if (isTouchDevice) return;

    setIsResizing(true);
  };

  const handleMouseUp = useCallback(() => {
    if (isTouchDevice) return;

    setIsResizing(false);
    setHandleHeight(handleRef.current);
    document.body.style.userSelect = 'initial'; // Re-enable selection
  }, [handleRef]);

  const handleMouseMove = (e) => {
    if (isTouchDevice) return;

    if (isResizing) {
      document.body.style.userSelect = 'none'; // Make elements unselectable while dragging
      const newWidth = e.clientX - resizableRef.current.getBoundingClientRect().left;
      const minWidth = 400;
      const maxWidth = resizableRef?.current?.clientWidth - 400;
      const limitedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));

      setWidth(limitedWidth);
      persistToLocalStorage(limitedWidth);
    }
  };

  const handleTouchStart = (e) => {
    setIsResizing(true);
  };

  const handleTouchMove = (e) => {
    if (isResizing) {
      document.body.style.userSelect = 'none'; // Prevent selection on touch devices
      const touch = e.touches[0]; // Get the first touch point
      const newWidth = touch.clientX - resizableRef.current.getBoundingClientRect().left;
      const minWidth = 400;
      const maxWidth = resizableRef?.current?.clientWidth - 400;
      const limitedWidth = Math.max(minWidth, Math.min(newWidth, maxWidth));

      setWidth(limitedWidth);
      persistToLocalStorage(limitedWidth);
    }
  };

  const handleTouchEnd = useCallback(() => {
    setIsResizing(false);
    setHandleHeight(handleRef.current);
    document.body.style.userSelect = 'initial'; // Re-enable selection on touch end
  }, [handleRef]);

  const handleDoubleClick = () => {
    setWidth(0);
    persistToLocalStorage(0);
  };

  useEffect(() => {
    try {
      const widthFromLocalStorage = localStorage.getItem('resizable-area-width');
      if (widthFromLocalStorage) {
        setWidth(parseInt(widthFromLocalStorage));
      }
    } catch (error) {
      console.log('error restoring from local storage');
    }
  }, [disabled]);

  useEffect(() => {
    if (!handleRef?.current) return;

    const handleResize = () => handleWindowResize(handleRef.current, setWidth);

    if (!isTouchDevice) {
      // Add event listeners for both mouse and touch events
      handleRef.current.addEventListener('mousedown', handleMouseDown);
    }

    handleRef.current.addEventListener('touchstart', handleTouchStart);
    handleRef.current.addEventListener('dblclick', handleDoubleClick);

    window.addEventListener('resize', handleResize);

    setHandleHeight(handleRef.current);

    return () => {
      // Clean up event listeners
      if (!isTouchDevice) {
        handleRef?.current?.removeEventListener('mousedown', handleMouseDown);
      }

      handleRef?.current?.removeEventListener('touchstart', handleTouchStart);
      handleRef?.current?.removeEventListener('dblclick', handleDoubleClick);

      window.removeEventListener('resize', handleResize);
    };
  }, [handleRef, children, isTouchDevice]);

  return (
    <Container
      ref={resizableRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {children({ width: disabled ? 0 : width })}
    </Container>
  );
};

export default Resizable;